/**
 * <TutorStudentWorkViewer> — le travail d'un élève, ouvert en grand pendant qu'on y répond (organism).
 *
 * La question technique de la candidature : le champion de l'étape montre l'énoncé d'un élève et sa copie, et le candidat
 * explique ses erreurs, en vidéo, « comme s'il s'adressait à l'élève ». L'ancien écran ne les montrait
 * qu'en vignettes de 100 px, qui s'ouvraient par-dessus la caméra : on ne pouvait pas lire la copie ET
 * se voir. Ici, le travail reste ouvert : la copie (ou l'énoncé, d'un geste) remplit la place, et se
 * zoome du doigt (`<PinchZoom>`), comme l'énoncé d'une activité ; au web, où la souris ne pince pas,
 * par deux boutons, comme `<ImageDisplay>`. Chaque page s'ouvre entière. Ces boutons sont la deuxième
 * copie de ceux d'`<ImageDisplay>` : à la troisième surface zoomable, ils deviennent une pièce partagée
 * (ou `<PinchZoom>` apprend la molette).
 *
 * Il ne sait rien de la caméra : `<TutorVideoRecorder>` le pose à côté du viseur, et la question
 * vidéo pendant la réflexion. `tone="night"` sur la nuit de l'enregistrement.
 *
 * Contrôlé : la page montrée (`part`) arrive en props, le changement remonte.
 */
import React, { useRef } from "react"
import { Image, Platform, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutorVideoQuestionWork } from "../../../ports/primary/ui/types/tutorCandidature"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { PinchZoom, type PinchZoomHandle } from "../../atoms/PinchZoom"
import { Text } from "../../atoms/typography/Text"

const WORK_VIEWER_TEXTS = {
    copy: "Sa copie",
    statement: "L'énoncé",
    pinch: "Pince pour zoomer",
    zoomIn: "Zoomer",
    zoomOut: "Dézoomer",
}

const IS_WEB = Platform.OS === "web"

export type TutorStudentWorkPart = "copy" | "statement"

export type TutorStudentWorkViewerProps = {
    work: UITutorVideoQuestionWork
    part: TutorStudentWorkPart
    onChangePart: (part: TutorStudentWorkPart) => void
    /** `night` sur la nuit de l'enregistrement. Défaut `day`. */
    tone?: "day" | "night"
    testID?: string
}

/**
 * La copie à côté d'une colonne : la réflexion et l'enregistrement d'une question technique la posent au
 * même seuil et avec la même colonne, pour qu'on la retrouve où on l'a laissée.
 */
export const WORK_SPLIT_AT = 820
export const WORK_SIDE_WIDTH = 400

/** Mémorisé : le décompte qui l'entoure se re-rend à chaque seconde, la copie n'a pas à le suivre. */
export const TutorStudentWorkViewer = React.memo(function TutorStudentWorkViewer(props: TutorStudentWorkViewerProps) {
    const { work, part, onChangePart, tone = "day", testID } = props
    const uri = part === "copy" ? work.copyUri : work.statementUri
    const zoomRef = useRef<PinchZoomHandle>(null)
    const ink = tone === "night" ? "text-on-night" : "text-muted"

    return (
        <View style={styles.root} testID={testID}>
            <Row gap="2" justify="between" style={styles.bar}>
                <Row gap="2">
                    <ChoiceChip
                        variant="pill"
                        label={WORK_VIEWER_TEXTS.copy}
                        selected={part === "copy"}
                        onPress={() => onChangePart("copy")}
                    />
                    <ChoiceChip
                        variant="pill"
                        label={WORK_VIEWER_TEXTS.statement}
                        selected={part === "statement"}
                        onPress={() => onChangePart("statement")}
                    />
                </Row>
                {IS_WEB ? (
                    <Row gap="1">
                        <IconButton
                            iconName="search-minus"
                            appearance="transparent"
                            size={36}
                            iconColor={ink}
                            onPress={() => zoomRef.current?.zoomOut()}
                            accessibilityLabel={WORK_VIEWER_TEXTS.zoomOut}
                        />
                        <IconButton
                            iconName="search-plus"
                            appearance="transparent"
                            size={36}
                            iconColor={ink}
                            onPress={() => zoomRef.current?.zoomIn()}
                            accessibilityLabel={WORK_VIEWER_TEXTS.zoomIn}
                        />
                    </Row>
                ) : (
                    <Text size="text-xs" color={ink}>
                        {WORK_VIEWER_TEXTS.pinch}
                    </Text>
                )}
            </Row>
            <View style={styles.paper}>
                <PinchZoom key={part} ref={zoomRef} style={styles.zoom}>
                    <Image
                        source={{ uri }}
                        resizeMode="contain"
                        style={styles.image}
                        accessibilityLabel={part === "copy" ? WORK_VIEWER_TEXTS.copy : WORK_VIEWER_TEXTS.statement}
                    />
                </PinchZoom>
            </View>
        </View>
    )
})

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    bar: { paddingBottom: theme.spacing["3"], flexWrap: "wrap", alignItems: "center" },
    // Une photo de copie reste sur blanc, de jour comme de nuit : c'est une feuille.
    paper: {
        flex: 1,
        minHeight: 0,
        backgroundColor: theme.colors.white,
        borderRadius: theme.radius.md,
        overflow: "hidden",
        borderWidth: 1,
        borderColor: theme.colors["border-card"],
    },
    zoom: { flex: 1 },
    image: { width: "100%", height: "100%" },
}))
